/**
 * ImageModal.js - 이미지 확대/축소/회전/슬라이드 모달
 * 
 * 주요 기능:
 * 1. 이미지 상세 보기 (모달 형태) - Portal 사용으로 최상위 렌더링
 * 2. 줌 인/아웃 기능 (기본 75%, 25% ~ 300% 범위)
 * 3. 이미지 회전 기능 (90도 단위)
 * 4. 썸네일 네비게이션 지원 (하단 썸네일 클릭으로 이동)
 * 5. 로딩 상태 표시 및 에러 처리
 * 6. 키보드 단축키 지원 (ESC: 닫기)
 * 7. 배경 클릭 시 닫기
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import useScrollLock from '../../hooks/useScrollLock';
import './ImageModal.css';

const ImageModal = ({
  isOpen,
  onClose,
  imageUrl,        // 단일 이미지 (하위 호환)
  images = [],     // 여러 이미지 배열
  initialIndex = 0,
  altText = '',
  title = '',
  showControls = true,
}) => {
  useScrollLock(isOpen);

  // 이미지 리스트 구성
  const imageList = images.length > 0
    ? images
    : imageUrl
      ? [{ url: imageUrl, alt: altText, title: title }]
      : [];

  // 현재 보고 있는 이미지의 인덱스 상태
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // 이미지 변환 상태 관리 (기본 배율 75%)
  const [scale, setScale] = useState(0.75);
  const [rotation, setRotation] = useState(0);

  // 이미지 로딩 및 에러 상태 관리
  // 이미지 변경 시 '즉시' 로딩 상태로 전환하지 않고 이전 이미지를 유지하다가
  // 새 이미지가 로드되면 교체하는 방식도 고려 가능하나,
  // 여기서는 사용자 요청대로 '깜빡임' 최소화를 위해 고정된 컨테이너 크기 내에서 처리
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // 썸네일 바 자동 스크롤을 위한 Ref
  const thumbRef = useRef(null);

  // 다중 이미지 여부
  const hasMultipleImages = imageList.length > 1;

  /**
   * 특정 인덱스의 이미지로 이동
   */
  const handleSelectImage = useCallback((index) => {
    if (index === currentIndex) return;
    setCurrentIndex(index);
    // 이미지 변경 시 상태 초기화 (배율 75% 복귀)
    setScale(0.75);
    setRotation(0);
    setIsLoading(true);
    setHasError(false);
  }, [currentIndex]);

  /**
   * 모달 열림/닫힘 및 초기 인덱스 변경 감지
   */
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setScale(0.75); // 초기 배율 75%
      setRotation(0);
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen, initialIndex]);

  /**
   * 키보드 이벤트 (ESC 닫기, 좌우 화살표 썸네일 이동)
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasMultipleImages) {
        handleSelectImage(currentIndex > 0 ? currentIndex - 1 : imageList.length - 1);
      } else if (e.key === 'ArrowRight' && hasMultipleImages) {
        handleSelectImage(currentIndex < imageList.length - 1 ? currentIndex + 1 : 0);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, hasMultipleImages, currentIndex, imageList.length, handleSelectImage]);

  /**
   * 활성 썸네일이 화면에 보이도록 스크롤 조정
   */
  useEffect(() => {
    if (isOpen && thumbRef.current) {
      const activeBtn = thumbRef.current.querySelector('.image-modal-thumb-btn.active');
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentIndex, isOpen]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => {
    setScale(0.75); // 리셋 시에도 75%로
    setRotation(0);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      e.stopPropagation();
      onClose(e);
    }
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    onClose(e);
  };

  if (!isOpen || imageList.length === 0) return null;

  const currentImage = imageList[currentIndex];
  const currentUrl = currentImage?.url || '';
  const currentAlt = currentImage?.alt || altText || '';
  const currentTitle = currentImage?.title || title || '';

  return ReactDOM.createPortal(
    <div className="image-modal-overlay" onClick={handleBackdropClick}>
      <div className="image-modal-container">
        {/* 1. 헤더 */}
        <div className="image-modal-header">
          <div className="image-modal-title">
            {currentTitle && <h3>{currentTitle}</h3>}
            {hasMultipleImages && (
              <span className="image-modal-counter">
                {currentIndex + 1} / {imageList.length}
              </span>
            )}
          </div>
          <button
            className="image-modal-close-btn"
            onClick={handleCloseClick}
            aria-label="모달 닫기"
          >
            <X size={24} />
          </button>
        </div>

        {/* 2. 컨트롤 바 */}
        {showControls && (
          <div className="image-modal-controls">
            <button
              className="image-modal-control-btn"
              onClick={handleZoomOut}
              disabled={scale <= 0.25}
              title="축소"
            >
              <ZoomOut size={20} />
            </button>
            <span className="image-modal-zoom-info">
              {Math.round(scale * 100)}%
            </span>
            <button
              className="image-modal-control-btn"
              onClick={handleZoomIn}
              disabled={scale >= 3}
              title="확대"
            >
              <ZoomIn size={20} />
            </button>
            <button
              className="image-modal-control-btn"
              onClick={handleRotate}
              title="회전"
            >
              <RotateCw size={20} />
            </button>
            <button
              className="image-modal-control-btn"
              onClick={handleReset}
              title="기본 크기 (75%)"
            >
              리셋
            </button>
          </div>
        )}

        {/* 3. 이미지 콘텐츠 (화살표 제거됨) */}
        <div className="image-modal-content">
          {isLoading && (
            <div className="image-modal-loading">
              <div className="image-modal-spinner"></div>
              <p>이미지를 불러오는 중...</p>
            </div>
          )}

          {hasError && (
            <div className="image-modal-error">
              <p>이미지를 불러올 수 없습니다.</p>
              <button onClick={() => window.location.reload()}>
                다시 시도
              </button>
            </div>
          )}

          {currentUrl && !hasError && (
            <div className="image-modal-image-container">
              <img
                src={currentUrl}
                alt={currentAlt}
                className="image-modal-image"
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  // 로딩 중일 때 숨기면 깜빡임이 심할 수 있으므로, 투명도 조절 방식 등 고려 가능하나
                  // 여기서는 단순 display 처리 (loading 표시가 중앙에 뜨므로)
                  visibility: isLoading ? 'hidden' : 'visible'
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
                draggable={false}
              />
            </div>
          )}
        </div>

        {/* 4. 썸네일 네비게이션 (하단 푸터 대체) */}
        {hasMultipleImages && (
          <div className="image-modal-thumbnails" ref={thumbRef}>
            {imageList.map((img, idx) => (
              <button
                key={idx}
                className={`image-modal-thumb-btn ${currentIndex === idx ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectImage(idx);
                }}
              >
                <img
                  src={img.url}
                  alt={`썸네일 ${idx + 1}`}
                  className="image-modal-thumb-img"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ImageModal;
